// Records one kind of media (audio or video) from the local stream in full
// quality, handing over a numbered chunk every couple of seconds. Chunks of
// one recorder concatenate, in order, into a playable file.

export type TrackKind = 'audio' | 'video';

/** Chunk length. Short enough that little is at risk, long enough to be cheap. */
export const TIMESLICE_MS = 2000;
export const AUDIO_BITRATE = 256_000;
export const VIDEO_BITRATE = 4_000_000;

const CANDIDATES: Record<TrackKind, string[]> = {
  audio: ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4;codecs=opus', 'audio/mp4'],
  video: ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4;codecs=avc1', 'video/mp4'],
};

/** The best format this browser can record (Safari records mp4), or null. */
export function pickMime(kind: TrackKind, isSupported: (mime: string) => boolean): string | null {
  return CANDIDATES[kind].find((m) => isSupported(m)) ?? null;
}

export interface TrackRecorderOptions {
  stream: MediaStream;
  kind: TrackKind;
  mime: string;
  onChunk: (seq: number, blob: Blob) => void;
  /** When recording actually began (local high-res ms). */
  onStart?: (localMs: number) => void;
  onError?: (err: unknown) => void;
  now?: () => number;
  Recorder?: typeof MediaRecorder;
  timesliceMs?: number;
}

export class TrackRecorder {
  private rec: MediaRecorder;
  private seq = 0;
  private stopped: Promise<number> | null = null;

  constructor(private o: TrackRecorderOptions) {
    const R = o.Recorder ?? MediaRecorder;
    // Only this kind's tracks: audio and video go to separate files.
    const tracks = o.kind === 'audio' ? o.stream.getAudioTracks() : o.stream.getVideoTracks();
    this.rec = new R(new MediaStream(tracks), {
      mimeType: o.mime,
      ...(o.kind === 'audio' ? { audioBitsPerSecond: AUDIO_BITRATE } : { videoBitsPerSecond: VIDEO_BITRATE }),
    });
    const now = o.now ?? (() => performance.timeOrigin + performance.now());
    this.rec.onstart = () => o.onStart?.(now());
    this.rec.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) o.onChunk(this.seq++, e.data);
    };
    this.rec.onerror = (e) => o.onError?.((e as unknown as { error?: unknown }).error ?? e);
  }

  get kind(): TrackKind {
    return this.o.kind;
  }

  start(): void {
    this.rec.start(this.o.timesliceMs ?? TIMESLICE_MS);
  }

  /** Stops; resolves with the last chunk's seq (-1 if none) once it's out. */
  stop(): Promise<number> {
    if (!this.stopped) {
      this.stopped = new Promise((resolve) => {
        if (this.rec.state === 'inactive') return resolve(this.seq - 1);
        // The final chunk is delivered before onstop.
        this.rec.onstop = () => resolve(this.seq - 1);
        this.rec.stop();
      });
    }
    return this.stopped;
  }
}
